import { onCleanup } from 'solid-js'
import type { ObjectContext } from '../project/objects'
import type { SelectionContext } from './selection'
import type { DragContext } from './drag'
import type { HistoryContext } from '../project/history'
import { removeObjectCmd } from '../project/commands'
import { keepsSelection, screenToSVG } from './utils'

export function InputHandler(P: { objects: ObjectContext, selection: SelectionContext, drag: DragContext, history: HistoryContext }): null {
	const selCtx  = P.selection
	const objCtx  = P.objects
	const dragCtx = P.drag
	const histCtx = P.history

	// ── Mouse ─────────────────────────────────────────────────────────────
	function onDown(e: MouseEvent): void {
		if (!selCtx.selected() && !selCtx.editing()) return
		if (keepsSelection(e.target)) return
		selCtx.deselect()
	}

	function onMove(e: MouseEvent): void {
		const { drag, rsz } = dragCtx.dragState
		if (drag) {
			const cur = screenToSVG(dragCtx.svgRef(), { x: e.clientX, y: e.clientY })
			drag.onUpdate(cur.x - drag.start.x, cur.y - drag.start.y)
		}
		if (rsz) {
			const cur = screenToSVG(dragCtx.svgRef(), { x: e.clientX, y: e.clientY })
			rsz.onUpdate(cur.x - rsz.start.x, cur.y - rsz.start.y)
		}
	}
	function onUp(): void {
		dragCtx.dragState.drag?.onEnd?.()
		dragCtx.dragState.rsz?.onEnd?.()
		dragCtx.dragState.drag = null
		dragCtx.dragState.rsz  = null
	}

	// ── Keyboard ──────────────────────────────────────────────────────────
	function isTypingElsewhere(): boolean {
		const el = document.activeElement
		if (!el) return false
		const tag = el.tagName
		return tag === 'INPUT' || tag === 'TEXTAREA' || (el as HTMLElement).isContentEditable
	}

	function onKey(e: KeyboardEvent): void {
		if (isTypingElsewhere()) return
		if (e.key === 'Escape') {
			if (selCtx.editing()) selCtx.stopEdit()
			else {
				const parentId = selCtx.selected() ? objCtx.findParentOf(selCtx.selected()!) : null
				if (parentId) selCtx.select(parentId)
				else selCtx.deselect()
			}
		}
		if ((e.key === 'Delete' || e.key === 'Backspace') && selCtx.selected() && !selCtx.editing()) {
			histCtx.execute(removeObjectCmd(objCtx, selCtx, selCtx.selected()!))
		}
		if (e.ctrlKey && e.key === 'z') { e.preventDefault(); histCtx.undo() }
		if (e.ctrlKey && e.key === 'y') { e.preventDefault(); histCtx.redo() }
		if (e.key === 'Shift') selCtx.setShiftHeld(true)
	}
	function onKeyUp(e: KeyboardEvent): void {
		if (e.key === 'Shift') selCtx.setShiftHeld(false)
	}

	// Document listeners with cleanup — no mounted DOM needed, so this binds to the
	// setup owner rather than waiting on onMount.
	document.addEventListener('mousedown', onDown)
	document.addEventListener('mousemove', onMove)
	document.addEventListener('mouseup',   onUp)
	document.addEventListener('keydown',   onKey)
	document.addEventListener('keyup',     onKeyUp)
	onCleanup(() => {
		document.removeEventListener('mousedown', onDown)
		document.removeEventListener('mousemove', onMove)
		document.removeEventListener('mouseup',   onUp)
		document.removeEventListener('keydown',   onKey)
		document.removeEventListener('keyup',     onKeyUp)
	})

	return null
}
